import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LocationField from '../components/LocationField';
import { CLASSIFIED_CATEGORIES, PRICE_TYPES, CLASSIFIED_TAXONOMY, categoryChildren, mergeCustomCategories } from '../data/classifiedsCategories';
import { resolveAssetUrl } from '../lib/assets';
import { ArrowLeft, Upload, X, Loader2, Tag, Image as ImageIcon, Truck, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGES = 10;

export default function CreateClassifiedPage() {
  const { tr } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cats, setCats] = useState(CLASSIFIED_CATEGORIES);
  const [proposeNew, setProposeNew] = useState(false);
  const [newCat, setNewCat] = useState({ label: '', parent: '' });
  const [catSel, setCatSel] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', category: '', price: '', price_type: 'fixed',
    images: [], contact_phone: '',
    region: '', location_id: '', location_name: '', location_district: '', location_district_slug: '', lat: null, lng: null,
    delivery: false, pickup: true, location_text: '',
  });

  useEffect(() => {
    axios.get(`${API}/classifieds/meta/categories`).then(({ data }) => setCats(data.categories || CLASSIFIED_CATEGORIES)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/classifieds/${id}`).then(({ data }) => {
        const f = data.fulfillment || {};
        setForm({
          title: data.title || '', description: data.description || '', category: data.category || '',
          price: data.price ?? '', price_type: data.price_type || 'fixed',
          images: data.images || [], contact_phone: data.contact_phone || '',
          region: data.region || '', location_id: data.location_id || '', location_name: data.location_name || '',
          location_district: data.location_district || '', location_district_slug: data.location_district_slug || '',
          lat: data.lat ?? null, lng: data.lng ?? null,
          delivery: !!f.delivery, pickup: f.pickup !== false, location_text: f.location_text || '',
        });
        setCatSel(Array.isArray(data.category_path) && data.category_path.length ? data.category_path : (data.category ? [data.category] : []));
        setLoading(false);
      }).catch(() => { toast.error(tr('Nevarēja ielādēt', 'Could not load', 'Не удалось загрузить')); navigate('/mani-sludinajumi'); });
    } else if (user) {
      setForm((f) => ({ ...f, contact_phone: user.phone || '', region: f.region || user.region || '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Merged taxonomy tree (fixed + admin-approved custom categories).
  const catTree = useMemo(() => mergeCustomCategories(cats.filter((c) => c.custom), CLASSIFIED_TAXONOMY), [cats]);
  const leafCat = catSel[catSel.length - 1] || '';

  // Cascading levels to render: top → sub → sub-sub ... (stops at a leaf).
  const catLevels = [];
  {
    let opts = categoryChildren(null, catTree);
    let i = 0;
    while (opts && opts.length) {
      const selected = catSel[i] || '';
      catLevels.push({ opts, selected, i });
      if (!selected) break;
      opts = categoryChildren(selected, catTree);
      i += 1;
    }
  }

  // Flat node list (with breadcrumb labels) for the "propose new" parent picker.
  const flatNodes = [];
  const walkFlat = (nodes, prefix) => nodes.forEach((n) => {
    const label = prefix ? `${prefix} › ${tr(n.lv, n.en, n.ru)}` : tr(n.lv, n.en, n.ru);
    flatNodes.push({ id: n.id, label });
    if (n.children) walkFlat(n.children, label);
  });
  walkFlat(catTree, '');

  const pickLevel = (i, v) => {
    if (i === 0 && v === '__new__') { setProposeNew(true); setCatSel([]); return; }
    setProposeNew(false);
    setCatSel((prev) => [...prev.slice(0, i), v]);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    for (const file of files) {
      if (form.images.length >= MAX_IMAGES) { toast.error(tr('Maksimums 10 foto', 'Max 10 photos', 'Максимум 10 фото')); break; }
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) { toast.error(tr('Fails pārāk liels (max 5MB)', 'File too large (max 5MB)', 'Файл слишком большой (макс 5MB)')); continue; }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      } catch { toast.error(tr('Kļūda augšupielādējot', 'Upload error', 'Ошибка загрузки')); }
      setUploading(false);
    }
  };

  const removeImage = (idx) => set({ images: form.images.filter((_, i) => i !== idx) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.title.trim().length < 2) return toast.error(tr('Ievadi nosaukumu', 'Enter a title', 'Введите название'));
    if (proposeNew ? !newCat.label.trim() : !leafCat) return toast.error(tr('Izvēlies vai ierosini kategoriju', 'Pick or propose a category', 'Выберите или предложите категорию'));
    if (form.images.length < 1) return toast.error(tr('Pievieno vismaz 1 foto', 'Add at least 1 photo', 'Добавьте хотя бы 1 фото'));
    if (form.price_type !== 'swap' && (form.price === '' || Number(form.price) < 0)) return toast.error(tr('Norādi cenu', 'Enter a price', 'Укажите цену'));

    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: proposeNew ? (newCat.parent || 'cits') : leafCat,
      price_type: form.price_type,
      price: form.price_type === 'swap' ? null : Number(form.price),
      images: form.images,
      contact_phone: form.contact_phone.trim() || null,
      region: form.region || null,
      location_id: form.location_id || null,
      location_name: form.location_name || null,
      location_district: form.location_district || null,
      location_district_slug: form.location_district_slug || null,
      lat: form.lat, lng: form.lng,
      fulfillment: { delivery: form.delivery, pickup: form.pickup, location_text: form.location_text.trim() || null },
    };
    if (proposeNew && newCat.label.trim()) payload.new_category = { label: newCat.label.trim(), parent: newCat.parent || null };
    try {
      let res;
      if (isEdit) res = await axios.put(`${API}/classifieds/${id}`, payload);
      else res = await axios.post(`${API}/classifieds`, payload);
      if (res.data.status === 'pending') {
        toast.success(tr('Sludinājums iesniegts! Tas tiks publicēts, kad admins apstiprinās jauno kategoriju.', 'Submitted! It will go live once an admin approves the new category.', 'Отправлено! Будет опубликовано после одобрения категории.'), { duration: 7000 });
        navigate('/mani-sludinajumi');
      } else {
        toast.success(isEdit ? tr('Saglabāts!', 'Saved!', 'Сохранено!') : tr('Sludinājums ievietots!', 'Listing posted!', 'Объявление размещено!'));
        navigate(`/sludinajums/${res.data.slug || res.data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr('Kļūda', 'Error', 'Ошибка'));
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]"><Loader2 className="w-8 h-8 animate-spin text-[#2F7E79]" /></div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="create-classified-page">
      <div className="max-w-2xl mx-auto px-5 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {tr('Atpakaļ', 'Back', 'Назад')}
        </button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#E6F2F0] flex items-center justify-center"><Tag className="w-6 h-6 text-[#2F7E79]" /></div>
          <h1 className="font-outfit text-3xl font-bold text-[#121212]">{isEdit ? tr('Rediģēt sludinājumu', 'Edit listing', 'Редактировать') : tr('Jauns sludinājums', 'New listing', 'Новое объявление')}</h1>
        </div>

        <form onSubmit={submit} className="space-y-6">
          {/* Images */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
            <label className="block text-sm font-semibold text-[#121212] mb-3">{tr('Fotogrāfijas', 'Photos', 'Фотографии')} <span className="text-[#8A8A8A] font-normal">({form.images.length}/{MAX_IMAGES})</span></label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[#E5E4E0] group" data-testid={`image-thumb-${i}`}>
                  <img src={resolveAssetUrl(img)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute top-1 left-1 text-[10px] font-bold bg-[#2F7E79] text-white px-1.5 py-0.5 rounded">{tr('Galvenā', 'Main', 'Главное')}</span>}
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${i}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {form.images.length < MAX_IMAGES && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="aspect-square rounded-xl border-2 border-dashed border-[#D5D3CC] flex flex-col items-center justify-center text-[#8A8A8A] hover:border-[#2F7E79] hover:text-[#2F7E79] transition-colors" data-testid="add-image-btn">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Upload className="w-6 h-6 mb-1" /><span className="text-xs">{tr('Pievienot', 'Add', 'Добавить')}</span></>}
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid="image-file-input" />
            {form.images.length === 0 && <p className="mt-2 text-xs text-[#8A8A8A] flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> {tr('Pievieno 1–10 fotogrāfijas', 'Add 1–10 photos', 'Добавьте 1–10 фото')}</p>}
          </div>

          {/* Title + category */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#121212] mb-1.5">{tr('Nosaukums', 'Title', 'Название')}</label>
              <Input value={form.title} onChange={(e) => set({ title: e.target.value })} maxLength={120}
                placeholder={tr('Piem., Lietots velosipēds', 'e.g., Used bicycle', 'Напр., Б/у велосипед')} data-testid="classified-title-input" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#121212] mb-1.5">{tr('Kategorija', 'Category', 'Категория')}</label>
              <div className="space-y-2" data-testid="classified-category-cascade">
                {catLevels.map((lvl) => (
                  <Select key={lvl.i} value={proposeNew && lvl.i === 0 ? '__new__' : (lvl.selected || undefined)}
                    onValueChange={(v) => pickLevel(lvl.i, v)}>
                    <SelectTrigger data-testid={`classified-category-select-${lvl.i}`}>
                      <SelectValue placeholder={lvl.i === 0 ? tr('Izvēlies kategoriju', 'Choose a category', 'Выберите категорию') : tr('Precizē apakškategoriju (nav obligāti)', 'Refine subcategory (optional)', 'Уточните подкатегорию')} />
                    </SelectTrigger>
                    <SelectContent>
                      {lvl.opts.map((c) => (
                        <SelectItem key={c.id} value={c.id} data-testid={`category-option-${c.id}`}>
                          {lvl.i === 0 ? `${c.emoji || '📦'} ` : ''}{tr(c.lv, c.en, c.ru)}
                        </SelectItem>
                      ))}
                      {lvl.i === 0 && <SelectItem value="__new__" data-testid="category-option-new">➕ {tr('Ierosināt jaunu kategoriju', 'Propose a new category', 'Предложить новую категорию')}</SelectItem>}
                    </SelectContent>
                  </Select>
                ))}
              </div>
              {proposeNew && (
                <div className="mt-3 p-3 rounded-xl bg-[#E6F2F0] border border-[#C9E3DF] space-y-3" data-testid="new-category-box">
                  <p className="text-xs text-[#2F7E79] font-medium">{tr('Jaunā kategorija nonāks pie administratora apstiprināšanai. Sludinājums tiks publicēts, kad to apstiprinās.', 'The new category goes to an admin for approval. Your listing publishes once approved.', 'Новая категория будет отправлена администратору. Объявление опубликуется после одобрения.')}</p>
                  <Input value={newCat.label} onChange={(e) => setNewCat((n) => ({ ...n, label: e.target.value }))} maxLength={60}
                    placeholder={tr('Jaunās (apakš)kategorijas nosaukums', 'New (sub)category name', 'Название новой (под)категории')} data-testid="new-category-label-input" />
                  <Select value={newCat.parent || 'none'} onValueChange={(v) => setNewCat((n) => ({ ...n, parent: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="new-category-parent-select"><SelectValue placeholder={tr('Virskategorija (nav obligāti)', 'Parent category (optional)', 'Родительская категория (опц.)')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{tr('— Bez virskategorijas —', '— No parent —', '— Без родителя —')}</SelectItem>
                      {flatNodes.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Price */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 space-y-4">
            <label className="block text-sm font-semibold text-[#121212]">{tr('Cena', 'Price', 'Цена')}</label>
            <div className="flex gap-2">
              {PRICE_TYPES.map((p) => (
                <button key={p.id} type="button" onClick={() => set({ price_type: p.id })}
                  className={`flex-1 h-10 rounded-full text-sm font-semibold transition-colors ${form.price_type === p.id ? 'bg-[#2F7E79] text-white' : 'bg-[#F3F2EE] text-[#5C5C5C] hover:bg-[#E6F2F0]'}`}
                  data-testid={`price-type-${p.id}`}>{tr(p.lv, p.en, p.ru)}</button>
              ))}
            </div>
            {form.price_type !== 'swap' && (
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C5C5C] font-semibold">€</span>
                <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set({ price: e.target.value })}
                  className="pl-9" placeholder="0.00" data-testid="classified-price-input" />
              </div>
            )}
          </div>

          {/* Description */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
            <label className="block text-sm font-semibold text-[#121212] mb-1.5">{tr('Apraksts', 'Description', 'Описание')}</label>
            <Textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={5} maxLength={4000}
              placeholder={tr('Stāvoklis, izmērs, detaļas...', 'Condition, size, details...', 'Состояние, размер, детали...')} data-testid="classified-description-input" />
          </div>

          {/* Location + contact */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 space-y-4">
            <label className="block text-sm font-semibold text-[#121212]">{tr('Atrašanās vieta & kontakti', 'Location & contact', 'Местоположение и контакты')}</label>
            <LocationField value={{ region: form.region, location_name: form.location_name, location_district: form.location_district, location_district_slug: form.location_district_slug, lat: form.lat, lng: form.lng }}
              onChange={(partial) => set(partial)} testid="classified-location" />
            <div>
              <label className="block text-xs font-semibold text-[#5C5C5C] mb-1.5">{tr('Tālrunis', 'Phone', 'Телефон')}</label>
              <Input value={form.contact_phone} onChange={(e) => set({ contact_phone: e.target.value })} maxLength={40}
                placeholder="+371 ..." data-testid="classified-phone-input" />
            </div>
          </div>

          {/* Fulfillment */}
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 space-y-4">
            <label className="block text-sm font-semibold text-[#121212]">{tr('Saņemšana', 'Fulfilment', 'Получение')}</label>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#5C5C5C] flex items-center gap-2"><Store className="w-4 h-4" /> {tr('Savākšana uz vietas', 'Pickup', 'Самовывоз')}</span>
              <Switch checked={form.pickup} onCheckedChange={(v) => set({ pickup: v })} data-testid="fulfillment-pickup-toggle" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#5C5C5C] flex items-center gap-2"><Truck className="w-4 h-4" /> {tr('Piegāde pieejama', 'Delivery available', 'Доставка доступна')}</span>
              <Switch checked={form.delivery} onCheckedChange={(v) => set({ delivery: v })} data-testid="fulfillment-delivery-toggle" />
            </div>
            <Input value={form.location_text} onChange={(e) => set({ location_text: e.target.value })} maxLength={160}
              placeholder={tr('Papildu piezīme par vietu (nav obligāti)', 'Extra location note (optional)', 'Доп. заметка (необязательно)')} data-testid="fulfillment-note-input" />
          </div>

          <Button type="submit" disabled={saving || uploading}
            className="w-full h-12 bg-[#2F7E79] hover:bg-[#276b66] text-white font-semibold rounded-full gap-2" data-testid="submit-classified-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            {isEdit ? tr('Saglabāt izmaiņas', 'Save changes', 'Сохранить') : tr('Ievietot sludinājumu', 'Post listing', 'Разместить')}
          </Button>
        </form>
      </div>
    </div>
  );
}
